import type * as models from '../models'

/**
 * Current non-data presentation prepared from status and tool config.
 *
 * The data component renders this view model instead of rows whenever loading,
 * error, or empty-state rules select a replacement presentation.
 */
export interface Status {
  readonly tool?: Status.Tool
  readonly description?: string
  readonly illustration: 'add-row' | 'error' | 'no-data'
  readonly loading: boolean
  readonly title: string
}

export namespace Status {
  /** Tool shown below an error or empty-state illustration. */
  export interface Tool {
    readonly label: string
    readonly onClick: () => void
  }

  /**
   * Resolves request and empty states, with loading taking precedence.
   *
   * The order is deliberate: loading suppresses stale rows and errors; errors
   * suppress empty-state tools; non-empty rows suppress the empty state. A
   * create tool is preferred for empty data, with retry as its fallback.
   */
  export function get(
    status: models.Status | undefined,
    createTool: models.Tool | undefined,
    rowCount: number,
  ): Status | undefined {
    if (status?.loading) {
      // Do not expose stale row tools while a replacement request is active.
      return {
        illustration: 'no-data',
        loading: true,
        title: '',
      }
    }

    if (status?.error) {
      // Error text is consumer-controlled, with stable defaults for the common
      // collection failure case.
      return {
        tool: status.onRetry
          ? {
              label: status.error.toolLabel ?? 'Refresh',
              onClick: status.onRetry,
            }
          : undefined,
        description:
          status.error.description ??
          'The data did not load properly. Usually refreshing fixes this problem. Try the button below. If the problem persist contact support.',
        illustration: 'error',
        loading: false,
        title: status.error.title ?? 'Something went wrong',
      }
    }

    if (rowCount > 0) {
      return undefined
    }

    const empty = status?.empty
    // Loading and error have already been handled. At this point the only
    // question is which empty-state tool is safe to expose.
    const availableCreateTool =
      createTool && !createTool.disabled && !createTool.loading
        ? createTool
        : undefined
    const tool = availableCreateTool
      ? {
          label: empty?.toolLabel ?? availableCreateTool.label ?? 'Get Started',
          onClick: availableCreateTool.onClick,
        }
      : status?.onRetry
        ? {
            label: empty?.toolLabel ?? 'Refresh',
            onClick: status.onRetry,
          }
        : undefined

    return {
      tool,
      description:
        empty?.description ??
        (availableCreateTool
          ? "There is no content here yet, but it doesn't have to stay that way! You can click the button below to go ahead and get started"
          : 'No data was found. Not what you expected? Try refreshing, check your source data, or contact support.'),
      illustration: availableCreateTool ? 'add-row' : 'no-data',
      loading: false,
      title:
        empty?.title ??
        (availableCreateTool
          ? 'Ready to get started? Click the button below'
          : 'Nothing to see here!'),
    }
  }
}
